import { useState, type FormEvent } from 'react'
import { Link, useSearch } from '@tanstack/react-router'
import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_RULES_HINT,
  describePasswordProblem,
} from '@mads/shared'
import { ErrorNote } from '@/components/Feedback'
import { Logo } from '@/components/Logo'
import { useResetPassword } from '@/lib/queries'

/**
 * Where every set-password link lands, whether it came from a brand-new
 * invite or a forgotten-password request — both are the same link.
 */
export function ResetPassword() {
  const { token } = useSearch({ from: '/reset-password' })
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const resetPassword = useResetPassword()

  function onSubmit(event: FormEvent) {
    event.preventDefault()

    // Length first, then the confirmation: telling someone their passwords
    // don't match when neither would have been accepted wastes a round.
    const tooWeak = describePasswordProblem(password)

    if (tooWeak !== null) {
      setProblem(tooWeak)
      return
    }

    if (password !== confirm) {
      setProblem('Those passwords don’t match.')
      return
    }

    setProblem(null)
    resetPassword.mutate({ token, password })
  }

  return (
    <div className="auth-split">
      <div className="auth-brand">
        <Logo layout="stacked" theme="white" size={52} />
      </div>

      <div className="auth-form-side">
        <form className="auth-form" onSubmit={onSubmit}>
          <h1>Set your password</h1>

          {resetPassword.isSuccess ? (
            <p>Your password has been updated.</p>
          ) : token === '' ? (
            <p className="error-note" role="alert">
              This link is missing its token — check you copied the whole link from the email.
            </p>
          ) : (
            <>
              {problem !== null ? (
                <div className="error-note" role="alert">
                  {problem}
                </div>
              ) : (
                <ErrorNote error={resetPassword.error} />
              )}

              <div className="field">
                <label htmlFor="password">New password</label>
                <input
                  id="password"
                  className="input"
                  type="password"
                  autoComplete="new-password"
                  minLength={PASSWORD_MIN_LENGTH}
                  required
                  aria-describedby="password-rules"
                  style={{ minHeight: 46 }}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
                <p id="password-rules" className="social-hint" style={{ marginTop: 6 }}>
                  {PASSWORD_RULES_HINT}
                </p>
              </div>

              <div className="field">
                <label htmlFor="confirm-password">Confirm password</label>
                <input
                  id="confirm-password"
                  className="input"
                  type="password"
                  autoComplete="new-password"
                  minLength={PASSWORD_MIN_LENGTH}
                  required
                  style={{ minHeight: 46 }}
                  value={confirm}
                  onChange={(event) => setConfirm(event.target.value)}
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-block"
                style={{ minHeight: 48 }}
                disabled={resetPassword.isPending}
              >
                {resetPassword.isPending ? 'Saving…' : 'Set password'}
              </button>
            </>
          )}

          <p className="social-hint">
            <Link to="/sign-in">Back to sign in</Link>
          </p>
        </form>
      </div>
    </div>
  )
}
